import React, { useState, useEffect, ReactElement, Fragment, useMemo } from "react";
import { gql, useQuery, useMutation } from "urql";
import { useParams, useLocation, Link } from "wouter";
import { cloneDeep, sortBy } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { Button } from "../../../components/src/Button";
import { Alert, AlertVariant } from "../../../components/src/Alert";

import { DateHelper, emptyObjectId } from "../../../common/src";
import { validIsin } from "../../../common/src/isin-validator";
import {
    TextField,
    NumberField,
    SelectField,
    CheckboxItem,
    SubmitButton,
    CopyClipboardButton,
    SearchListField,
    SearchMultipleSelectField
} from "../../../components/src/form";

import { isValidMongoDBObjectID } from "../../../common/src/utils";
import { useAlertTimeOut } from "../../../components/src/common/Utils";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { Page } from "../../../components/src/Page";
import { ModelModal } from "../components/ModelModal";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import {
    InstrumentCategory,
    CurrencyEnum,
    InstrumentProductTypeEnum,
    InstrumentStatusEnum,
    InstrumentTypeEnum,
    InstrumentModelTypeEnum,
    CollectionNameEnum,
    InstrumentProductTypeEnumDescriptions,
    Instrument,
    CicAreaEnum,
    CicTypeEnum,
    CicTypeEnumDescriptions
} from "../types.generated";
import { SYSTEM_PARTY_ID } from "../Constants";
import { defaultAlias } from "../components/alias";
import { MarkDownField } from "../components/form/MarkDownField";
import { REACT_APP_API_URI } from "../env";

const CIC_TYPE_OPTIONS: ({ _id: string; name: string } & {
    [key: string]: any;
})[] = Object.keys(CicTypeEnum).map((key) => ({
    _id: key,
    name: CicTypeEnumDescriptions[key]
}));

const defaultFormData = {
    name: "  ",
    clientIds: [SYSTEM_PARTY_ID],
    bloombergDailyDownload: false,
    bloombergTicker: "",
    bloombergQuoteMultiplier: 1.0,
    category: InstrumentCategory.None,
    cic: "",
    cicArea: CicAreaEnum.None,
    cicType: CicTypeEnum.None,
    creditRatings: [],
    aliases: [],
    currency: CurrencyEnum.SEK,
    exchange: "",
    mic: "",
    isin: "",
    benchmarkId: emptyObjectId,
    issuerId: emptyObjectId,
    issuerProgramId: emptyObjectId,
    longName: "",
    placeOfSettlementId: emptyObjectId,
    productType: InstrumentProductTypeEnum.None,
    quantityDecimals: 0,
    status: InstrumentStatusEnum.Confirmed
};

const GET_PARTIES = gql`
    query {
        creditRatingAgencies: parties(filter: { typeIn: CreditRatingAgency }) {
            _id
            name
        }
        issuers: parties(filter: { typeIn: Issuer }) {
            _id
            name
        }
        places: parties(filter: { typeIn: PlaceOfSettlement }) {
            _id
            name
        }
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
        }
    }
`;

const GET_ISSUERPROGRAMS = gql`
    query {
        issuerprograms {
            _id
            name
            issuerId
        }
    }
`;

const GET_ALIASES = gql`
    query {
        parties {
            _id
            name
        }
    }
`;

export const GET_INSTRUMENT = gql`
    query instrument($id: GraphQLObjectId!, $includeVersions: Boolean = false) {
        instrument(_id: $id) {
            _id
            clientIds
            name
            benchmarkId
            bloombergDailyDownload
            bloombergQuoteMultiplier
            bloombergTicker
            category
            cic
            cicArea
            cicType
            creditRatings {
                creditRatingAgencyId
                grade
            }
            aliases {
                collection
                documentId
                key
                value
                comment
            }
            currency
            exchange
            mic
            isin
            issuerId
            issuerProgramId
            longName
            placeOfSettlementId
            productType
            quantityDecimals
            type
            status
            modelType
            maturityDate
            updateTimestamp
            updateUserInfo {
                name
            }
            description
            versions @include(if: $includeVersions)
            extensions {
                _id
                clientId
                client {
                    _id
                    name
                }
                key
                value
                comment
            }
            model {
                _t
                accountId
                clientId
                expiryDate
                legs {
                    _t
                    calendars
                    call
                    creditSpread
                    currency
                    dayAdjustment
                    dayCount
                    exercise
                    expiry
                    fixingType
                    flatAdjustment
                    issuerId
                    longStub
                    maturityDate
                    maturityPeriod
                    multiplier
                    notional
                    payLeg
                    rate
                    rateIndexId
                    recoveryRate
                    relativeAdjustment
                    rollingDate
                    rollingPeriod
                    settlementType
                    spread
                    startDate
                    startDateOffset
                    startPeriod
                    strike
                    ultimateForwardRate
                    underlyingId
                    volatility
                    zeroFloor
                    cashFlows {
                        endDate
                        exDate
                        fixings {
                            endDate
                            date
                            rate
                            startDate
                        }
                        notional
                        payDate
                        startDate
                    }
                    underlyings {
                        instrumentId
                        weight
                        startPrice
                        quanto
                    }
                }
                objectId
                objectType
                notionalScaling
                quoteCurrency
                quoteType
                startDate
            }
        }

        timeseries(filter: { instrumentIdIn: [$id] }) {
            description
            type
            _id
        }

        lastValuations(instrumentIds: [$id]) {
            _id
            date
        }
    }
`;

const GET_INSTRUMENTS = gql`
    query {
        instruments {
            _id
            isin
            name
            longName
            bloombergTicker
            currency
        }
    }
`;

const CREATE_INSTRUMENT = gql`
    mutation createInstrument($input: CreateInstrumentInput!) {
        createInstrument(input: $input) {
            _id
        }
    }
`;

const UPDATE_INSTRUMENT = gql`
    mutation updateInstrument($input: UpdateInstrumentInput!) {
        updateInstrument(input: $input) {
            _id
        }
    }
`;

const DELETE_INSTRUMENT = gql`
    mutation deleteInstrument($_id: GraphQLObjectId!) {
        deleteInstrument(_id: $_id)
    }
`;

export function InstrumentPage(): ReactElement {
    const { id } = useParams<{ id: string }>();

    const [location, navigate] = useLocation();

    const [formData, setFormData] = useState(cloneDeep(defaultFormData));
    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [modelModal, setModelModal] = useState({ showModal: false });

    const isCreateMode = !!(id && id.toString() === "new");

    const [{ fetching: loadingInstruments, data: instrumentsData, error: errorInstruments }] = useQuery({
        query: GET_INSTRUMENTS,
        requestPolicy: "cache-first"
    });

    const [{ fetching: loadingParties, data: parties, error: errorParties }] = useQuery({
        query: GET_PARTIES,
        requestPolicy: "cache-first"
    });

    const [{ fetching: loadingIssuerPrograms, data: issuerprogramData, error: errorIssuerprograms }] = useQuery({
        query: GET_ISSUERPROGRAMS,
        requestPolicy: "cache-first"
    });

    const [{ fetching: loadingAliases, data: dataAliases, error: errorAliases }] = useQuery({
        query: GET_ALIASES,
        requestPolicy: "cache-first"
    });

    const [{ fetching: loading, data, error }, refetch] = useQuery({
        query: GET_INSTRUMENT,
        variables: { id },
        requestPolicy: "network-only",
        pause: isCreateMode
    });

    const [_, createInstrument] = useMutation(CREATE_INSTRUMENT);
    const [__, updateInstrument] = useMutation(UPDATE_INSTRUMENT);
    const [___, deleteInstrument] = useMutation(DELETE_INSTRUMENT);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (id && id !== "new" && data && data.instrument) {
            const instrument = {
                _id: data.instrument._id,
                benchmarkId: data.instrument.benchmarkId,
                clientIds: data.instrument.clientIds,
                issuerId: data.instrument.issuerId,
                issuerProgramId: data.instrument.issuerProgramId,
                name: data.instrument.name,
                bloombergDailyDownload: data.instrument.bloombergDailyDownload,
                bloombergTicker: data.instrument.bloombergTicker,
                bloombergQuoteMultiplier: parseFloat(data.instrument.bloombergQuoteMultiplier),
                category: data.instrument.category,
                cic: data.instrument.cic,
                cicArea: data.instrument.cicArea,
                cicType: data.instrument.cicType,
                creditRatings: data.instrument.creditRatings || [],
                currency: data.instrument.currency,
                exchange: data.instrument.exchange,
                mic: data.instrument.mic,
                isin: data.instrument.isin,
                longName: data.instrument.longName,
                placeOfSettlementId: data.instrument.placeOfSettlementId,
                productType: data.instrument.productType,
                type: data.instrument.type,
                quantityDecimals: data.instrument.quantityDecimals,
                status: data.instrument.status,
                description: data.instrument.description || "",
                aliases: data.instrument.aliases || []
            };
            setFormData(instrument);
        }
    }, [id, data]);

    const aliasOptions: { _id: string; name: string }[] = useMemo(() => {
        let options = [{ _id: "000000000000000000000000", name: CollectionNameEnum.None }];
        if (dataAliases) {
            options = [{ _id: "000000000000000000000000", name: CollectionNameEnum.None }, ...dataAliases.parties];
        }
        return options;
    }, [dataAliases]);

    const [jsonString, setJsonInput] = useState("");
    const [jsonValidation, setJsonValidation] = useState({ valid: true, message: "" });
    const onDismissValidationAlert = () => setJsonValidation({ valid: true, message: "" });

    if (loading || loadingParties || loadingIssuerPrograms || loadingInstruments || loadingAliases)
        return (
            <div>
                <div>Loading</div>
            </div>
        );

    if (error) return <pre>{JSON.stringify(error, null, 2)}</pre>;
    if (errorParties) return <pre>{JSON.stringify(errorParties, null, 2)}</pre>;
    if (errorIssuerprograms) return <pre>{JSON.stringify(errorIssuerprograms, null, 2)}</pre>;
    if (errorInstruments) return <pre>{JSON.stringify(errorInstruments, null, 2)}</pre>;
    if (errorAliases) return <pre>{JSON.stringify(errorAliases, null, 2)}</pre>;

    if (id !== "new" && !data.instrument) {
        return <div>Instrument not found</div>;
    }

    if (!isCreateMode && data && data.instrument) {
        if (!isValidMongoDBObjectID(id)) {
            return <Page header={"id: ''" + id + "'' is not a valid id format"} />;
        }
    }

    const creditRatingAgencies = parties.creditRatingAgencies;
    const issuers = cloneDeep(parties.issuers);
    const places = cloneDeep(parties.places);
    const clients = cloneDeep(parties.clients);

    let instruments = [];
    if (!loadingInstruments && instrumentsData) {
        instruments = cloneDeep(instrumentsData.instruments);
    }

    // ensure empty objects
    if (!issuers.map((i) => i._id).includes(emptyObjectId)) {
        issuers.unshift({ _id: emptyObjectId, name: "None" }); // ensure issuer None
    }
    if (!places.map((i) => i._id).includes(emptyObjectId)) {
        places.unshift({ _id: emptyObjectId, name: "None" }); // ensure places None
    }

    if (!instruments.map((i) => i._id).includes(emptyObjectId)) {
        instruments.unshift({ _id: emptyObjectId, name: "None" }); // ensure instrument None
    }

    const issuerprogramById = {};
    let issuerprograms = [];
    for (let i = 0; i < issuerprogramData.issuerprograms.length; i++) {
        const issuerprogram = issuerprogramData.issuerprograms[i];
        if (formData && formData.issuerId === issuerprogram.issuerId) {
            issuerprogramById[issuerprogram._id] = { name: issuerprogram.name, _id: issuerprogram._id };
            issuerprograms.push({ value: issuerprogram.name, name: issuerprogram.name, _id: issuerprogram._id, key: issuerprogram._id });
        }
    }

    issuerprograms = sortBy(issuerprograms, "name");
    issuerprograms = [{ key: emptyObjectId, name: "None", value: "None", _id: emptyObjectId }, ...issuerprograms];

    const cleanJsonInput = (input) => {
        let result = cloneDeep(input);
        result = recursivelyRemoveKey(result, "__typename");
        if (result.isin === "") {
            result.isin = null;
        }
        delete result.updateTimestamp;
        delete result.updateUserInfo;
        delete result.cic;

        delete result.modelType; // Field "modelType" is not defined by type UpdateInstrumentInput
        delete result.maturityDate; // Field "maturityDate" is not defined by type UpdateInstrumentInput.
        delete result.extensions; // Field "maturityDate" is not defined by type UpdateInstrumentInput.
        return result;
    };

    const validateJson = (json) => {
        try {
            const j = JSON.parse(json);
            const valid = typeof j === "object";
            if (jsonValidation.valid !== valid) {
                setJsonValidation({ valid: true, message: "" });
            }
        } catch (e) {
            setJsonValidation({ valid: false, message: "Invalid json: " + e.toString() });
        }
    };

    const handleJsonChange = (e) => {
        setJsonInput(e.target.value);
        validateJson(e.target.value);
    };

    const handleJsonSubmit = async (e) => {
        e.preventDefault();

        let input = JSON.parse(jsonString);

        // clean JSON
        input = cleanJsonInput(input);

        // update mutation
        await updateInstrument({ input })
            .then((result) => {
                if ("error" in result && result.error) {
                    const message = formikUrqlErrorFormater(result.error);
                    setAlert({ color: "danger", visible: true, message });
                } else {
                    refetch();
                    setAlert({
                        color: "success",
                        visible: true,
                        message: `'Update json' succeed. The instrument '${input.isin || input.name}' is updated!`
                    });
                }
                return true;
            })
            .catch((error) => {
                setAlert({ color: "danger", visible: true, message: error.toString() });
            });
    };

    const handleJsonReset = () => {
        const ins = cleanJsonInput(cloneDeep(data.instrument));
        const json = JSON.stringify(ins, null, 2);
        setJsonInput(json);
        validateJson(json);
    };

    const defaultCreditRating = {
        creditRatingAgencyId: creditRatingAgencies && creditRatingAgencies.length > 0 ? creditRatingAgencies[0]._id : "",
        grade: ""
    };

    const aliasLabel = (collection: CollectionNameEnum, documentId: string) => {
        if (collection === CollectionNameEnum.Party) {
            return <Link to={"/parties/" + documentId}>{collection}</Link>;
        } else {
            return collection;
        }
    };

    const startDate = DateHelper.dateToString(DateHelper.dateAddDays(new Date(), -365));

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete instrument with id " + modal.payload + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        deleteInstrument({ _id: modal.payload })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    const message = formikUrqlErrorFormater(result.error);
                                    setAlert({ color: "danger", visible: true, message });
                                } else {
                                    refetch();
                                    setAlert({
                                        color: "success",
                                        visible: true,
                                        message: `Instrument with id '${modal.payload}' was updated and deleted successfully!`
                                    });
                                }
                                return true;
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }}
                />
            ) : null}

            {modelModal.showModal && data && data.instrument ? (
                <ModelModal
                    isOpen={modelModal.showModal}
                    onClose={() => setModelModal({ showModal: false })}
                    instrument={data.instrument}
                />
            ) : null}

            <div className="mx-auto px-4 py-4">
                <h1>Instrument</h1>
                <div className="flex flex-wrap -mx-2">
                    {formData ? (
                        <Formik
                            enableReinitialize={true}
                            initialValues={formData}
                            validationSchema={Yup.object({
                                name: Yup.string().required().min(2),
                                isin: Yup.string()
                                    .nullable()
                                    .test("test-isin", "Invalid ISIN format", function (value) {
                                        if (!value) {
                                            return true;
                                        }
                                        const result = validIsin(value, { checkCountryCode: false });
                                        if (result[0]) {
                                            return true;
                                        } else {
                                            return this.createError({ message: result[1].message });
                                        }
                                    }),
                                bloombergQuoteMultiplier: Yup.number(),
                                creditRatings: Yup.array().of(
                                    Yup.object().shape({
                                        creditRatingAgencyId: Yup.string().required(),
                                        grade: Yup.string().matches(/^[0-9aA-zZ\-+]+$/, "Only  0-9aA-zZ\\-\\+ allowed")
                                    })
                                ),
                                mic: Yup.string().test("mic-length", "mic must be 0 or 4 characters", function (mic: string) {
                                    if (!mic || mic.length === 0 || mic.length === 4) {
                                        return true;
                                    }
                                    return false;
                                })
                            })}
                            validate={(validateValues) => {
                                const errors = {};

                                if (validateValues.name.startsWith(" ")) {
                                    errors["name"] = "Must not start with space";
                                }

                                const result = Object.keys(errors).length > 0 ? errors : {};
                                //console.error("validate", result);
                                return result;
                            }}
                            onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                                let input = submitValues;
                                input.mic = input.mic || "";
                                delete input.cic;
                                if (isCreateMode) {
                                    let newInstrId;
                                    delete input["_id"];

                                    await createInstrument({ input })
                                        .then((result) => {
                                            if ("error" in result && result.error) {
                                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                                setAlert({ color: "danger", visible: true, message });
                                            } else {
                                                newInstrId = result.data.createInstrument._id;
                                                setAlert({
                                                    color: "success",
                                                    visible: true,
                                                    message: `New instrument '${input.name}' created successfully!`
                                                });
                                                // redirect to edit instrument page on success
                                                if (newInstrId) {
                                                    const pathname = location.split("?")[0].split("#")[0];
                                                    const path = pathname.split("/");
                                                    path.pop();
                                                    path.push(newInstrId);
                                                    navigate(path.join("/"), { replace: true });
                                                }
                                            }
                                            return true;
                                        })
                                        .catch((error) => {
                                            setAlert({ color: "danger", visible: true, message: error.toString() });
                                        })
                                        .finally(() => {
                                            setSubmitting(false);
                                        });
                                } else {
                                    input = cleanJsonInput(input);
                                    delete input["model"];

                                    if (input.status && input.status === "Deleted") {
                                        setModal({ showModal: true, payload: input["_id"] });
                                    } else {
                                        await updateInstrument({ input })
                                            .then((result) => {
                                                if ("error" in result && result.error) {
                                                    const message = formikUrqlErrorFormater(result.error, setErrors);
                                                    setAlert({ color: "danger", visible: true, message });
                                                } else {
                                                    refetch();
                                                    setAlert({
                                                        color: "success",
                                                        visible: true,
                                                        message: `The instrument '${input.isin || input.name}' updated successfully!`
                                                    });
                                                }
                                                return true;
                                            })
                                            .catch((error) => {
                                                setAlert({ color: "danger", visible: true, message: error.toString() });
                                            })
                                            .finally(() => {
                                                setSubmitting(false);
                                            });
                                    }
                                }
                            }}
                        >
                            {({ isSubmitting, values: Values }) => {
                                const values = Values as Partial<Instrument>;
                                return (
                                    <div className="w-full px-2">
                                        <Form autoComplete="off" className="form">
                                            <div className="flex flex-wrap -mx-2">
                                                <div className="w-full lg:w-8/12 md:w-8/12 sm:w-9/12 px-2 mb-4">
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-full lg:w-full px-2 mb-4">
                                                            <TextField
                                                                name="name"
                                                                label="Name"
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-7/12 px-2 mb-4">
                                                            <TextField
                                                                name="longName"
                                                                label="Long name"
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="w-full sm:w-full md:w-3/12 lg:w-2/12 px-2 mb-4">
                                                            <SelectField
                                                                name="currency"
                                                                label="Currency"
                                                                options={Object.keys(CurrencyEnum).sort()}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="w-full sm:w-full md:w-3/12 lg:w-3/12 px-2 mb-4">
                                                            <SelectField
                                                                name="status"
                                                                label="Status"
                                                                options={Object.keys(InstrumentStatusEnum).sort()}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-4/12 lg:w-4/12 px-2 mb-4">
                                                            <SelectField
                                                                name="category"
                                                                label="Category (used by accounting)"
                                                                options={Object.keys(InstrumentCategory).sort()}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>

                                                        <div className="w-full sm:w-full md:w-3/12 lg:w-3/12 px-2 mb-4">
                                                            <SelectField
                                                                name="productType"
                                                                label="Product type"
                                                                options={Object.keys(InstrumentProductTypeEnum).sort()}
                                                                tooltips={InstrumentProductTypeEnumDescriptions}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>

                                                        <div className="w-full sm:w-full md:w-3/12 lg:w-3/12 px-2 mb-4">
                                                            <SelectField
                                                                name="type"
                                                                label="Type"
                                                                options={Object.keys(InstrumentTypeEnum).sort()}
                                                                className=""
                                                                disabled={isSubmitting || (id && id !== "new")}
                                                            />
                                                        </div>

                                                        <div className="w-full sm:w-full md:w-3/12 lg:w-3/12 px-2 mb-4">
                                                            <NumberField
                                                                name="quantityDecimals"
                                                                label="Quantity decimals"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <SelectField
                                                                name="placeOfSettlementId"
                                                                label={
                                                                    values.placeOfSettlementId !== emptyObjectId ? (
                                                                        <Link to={"/parties/" + values.placeOfSettlementId}>
                                                                            Place of settlement
                                                                        </Link>
                                                                    ) : (
                                                                        "Place of settlement"
                                                                    )
                                                                }
                                                                options={places.map((d) => ({ key: d._id, value: d.name }))}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>

                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <SearchListField
                                                                className=""
                                                                name="benchmarkId"
                                                                label={<Link to={"/instruments/" + values.benchmarkId}>Benchmark</Link>}
                                                                options={instruments}
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <SearchListField
                                                                className=""
                                                                name="issuerId"
                                                                label={<Link to={"/parties/" + values.issuerId}>Issuer</Link>}
                                                                options={issuers}
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <SelectField
                                                                className=""
                                                                name="issuerProgramId"
                                                                label={
                                                                    <Link to={"/issuerprograms/" + values.issuerProgramId}>
                                                                        Issuer program
                                                                    </Link>
                                                                }
                                                                options={issuerprograms}
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full xl:w-6/12 px-2 mt-3 mb-4">
                                                            <h5>Credit ratings</h5>
                                                            <FieldArray
                                                                name="creditRatings"
                                                                render={(arrayHelpers) => (
                                                                    <div>
                                                                        <Fragment>
                                                                            {values.creditRatings && values.creditRatings.length > 0 ? (
                                                                                values.creditRatings.map((item, index) => (
                                                                                    <div key={index}>
                                                                                        <div className="flex flex-wrap -mx-2">
                                                                                            <SelectField
                                                                                                className="w-full md:w-6/12 px-2 mb-4"
                                                                                                name={`creditRatings[${index}].creditRatingAgencyId`}
                                                                                                label={null}
                                                                                                options={creditRatingAgencies.map((d) => ({
                                                                                                    key: d._id,
                                                                                                    value: d.name
                                                                                                }))}
                                                                                                disabled={false}
                                                                                            />
                                                                                            <TextField
                                                                                                className="w-full md:w-4/12 px-2 mb-4"
                                                                                                name={`creditRatings[${index}].grade`}
                                                                                                label={null}
                                                                                                disabled={false}
                                                                                            />
                                                                                            <div className="w-full md:w-2/12 px-2 mb-4">
                                                                                                <Button
                                                                                                    variant="danger"
                                                                                                    size="sm"
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        arrayHelpers.remove(index)
                                                                                                    }
                                                                                                    style={{ height: "1.75rem" }}
                                                                                                >
                                                                                                    -
                                                                                                </Button>
                                                                                                <Button
                                                                                                    variant="primary"
                                                                                                    size="sm"
                                                                                                    className="ml-1"
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        arrayHelpers.insert(
                                                                                                            index,
                                                                                                            defaultCreditRating
                                                                                                        )
                                                                                                    }
                                                                                                    style={{ height: "1.75rem" }}
                                                                                                >
                                                                                                    +
                                                                                                </Button>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))
                                                                            ) : (
                                                                                <Button
                                                                                    variant="primary"
                                                                                    size="sm"
                                                                                    type="button"
                                                                                    onClick={() => arrayHelpers.push(defaultCreditRating)}
                                                                                >
                                                                                    Add New
                                                                                </Button>
                                                                            )}
                                                                        </Fragment>
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="w-full xl:w-6/12 px-2 mt-3">
                                                            <h5>Description</h5>
                                                            <MarkDownField
                                                                name="description"
                                                                graphqlApiBaseUrl={REACT_APP_API_URI}
                                                                label="Click to edit"
                                                                height={200}
                                                            />
                                                        </div>
                                                    </div>

                                                    {alert.visible ? (
                                                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                                                            {alert.message}
                                                        </Alert>
                                                    ) : null}

                                                    {values.type === InstrumentTypeEnum.PartyInstrument ? (
                                                        values.clientIds?.length > 0 ? (
                                                            <Link
                                                                className="block my-4"
                                                                style={{ fontSize: "1.5rem", fontWeight: "bold" }}
                                                                to={`/parties/${values.clientIds[0]}/instruments/${values._id}`}
                                                            >
                                                                {"Instrument needs to be modified here"}
                                                            </Link>
                                                        ) : (
                                                            <span>{"clientIds is empty"}</span>
                                                        )
                                                    ) : (
                                                        <SubmitButton
                                                            disabled={isSubmitting}
                                                            label={isCreateMode ? "Create" : "Update"}
                                                            className="btn btn-primary btn-lg"
                                                        />
                                                    )}

                                                    {data && data.instrument ? (
                                                        <div className="flex flex-wrap -mx-2 mt-3">
                                                            <div className="w-full lg:w-3/12 md:w-3/12 sm:w-6/12 px-2 mb-4">
                                                                <Link to={"/timeseries?instrumentId=" + data.instrument._id}>
                                                                    <h4>Timeseries</h4>
                                                                </Link>
                                                                {data.timeseries && data.timeseries.length > 0 ? (
                                                                    <div className="mt-2">
                                                                        {data.timeseries.map((timeseries, index) => {
                                                                            return (
                                                                                <div key={index}>
                                                                                    <Link to={"/timeseries/" + timeseries._id}>
                                                                                        {timeseries.type}
                                                                                    </Link>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : null}
                                                                <div className="mt-2">
                                                                    <Link to={"/timeseries/new?instrumentId=" + data.instrument._id}>
                                                                        {"New timeseries"}
                                                                    </Link>
                                                                </div>
                                                            </div>

                                                            <div className="w-full lg:w-3/12 md:w-3/12 sm:w-6/12 px-2 mb-4">
                                                                <h4>Valuation</h4>
                                                                <div className="mt-2">
                                                                    <Link to={"/valuations?instrumentIds=" + data.instrument._id}>
                                                                        {"Valuations"}
                                                                    </Link>
                                                                </div>
                                                                {data && data.lastValuations && data.lastValuations.length > 0 ? (
                                                                    <div className="mt-2">
                                                                        <Link to={"/valuation/" + data.lastValuations[0]._id}>
                                                                            {"Last: " + data.lastValuations[0].date}
                                                                        </Link>
                                                                    </div>
                                                                ) : null}
                                                                <div className="mt-2">
                                                                    <Link to={"/valuation/add?instrumentIdIn=" + data.instrument._id}>
                                                                        {"Add valuation"}
                                                                    </Link>
                                                                </div>
                                                            </div>

                                                            <div className="w-full lg:w-3/12 md:w-3/12 sm:w-6/12 px-2 mb-4">
                                                                <h4>Transactions</h4>
                                                                <div className="mt-2">
                                                                    <Link
                                                                        to={
                                                                            "/transactions?qInstrumentId=" +
                                                                            data.instrument._id +
                                                                            "&qStartDate=" +
                                                                            startDate
                                                                        }
                                                                    >
                                                                        {"Transactions"}
                                                                    </Link>
                                                                </div>
                                                                <div className="mt-2">
                                                                    <Link to={"/instrumentpositions?instrumentIds=" + data.instrument._id}>
                                                                        Positions
                                                                    </Link>
                                                                </div>
                                                            </div>

                                                            <div className="w-full lg:w-3/12 md:w-3/12 sm:w-6/12 px-2 mb-4">
                                                                <h4>Custodian Position</h4>
                                                                <div className="mt-2">
                                                                    <Link
                                                                        to={
                                                                            "/reconciliation/custodianpositions?instrumentIdIn=" +
                                                                            data.instrument._id
                                                                        }
                                                                    >
                                                                        Custodian Position
                                                                    </Link>
                                                                </div>
                                                            </div>

                                                            {data &&
                                                            data.instrument &&
                                                            data &&
                                                            data.instrument.modelType === InstrumentModelTypeEnum.PortfolioSwapX ? (
                                                                <div className="w-full lg:w-3/12 md:w-3/12 sm:w-6/12 px-2 mb-4">
                                                                    <h4>Swap constituents</h4>
                                                                    <div className="mt-2">
                                                                        <Link to={"/swapconstituents/" + data.instrument._id}>
                                                                            {"Swap constituents"}
                                                                        </Link>
                                                                    </div>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ) : null}

                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full xl:w-6/12 px-2 mb-4">
                                                            <h5>Aliases</h5>
                                                            <FieldArray
                                                                name="aliases"
                                                                render={(arrayHelpers) => (
                                                                    <Fragment>
                                                                        {values.aliases && values.aliases.length > 0 ? (
                                                                            values.aliases.map((alias, index) => (
                                                                                <div key={index} className="flex flex-wrap -mx-2 mb-4">
                                                                                    <div className="w-full md:w-10/12 px-2 mb-4">
                                                                                        <fieldset>
                                                                                            <legend>{index + 1}</legend>
                                                                                            <SelectField
                                                                                                name={`aliases[${index}].collection`}
                                                                                                label="Collection"
                                                                                                className="text-success"
                                                                                                options={["None", "Party"]}
                                                                                                disabled={isSubmitting}
                                                                                                size={5}
                                                                                            />
                                                                                            <SearchListField
                                                                                                className=""
                                                                                                name={`aliases[${index}].documentId`}
                                                                                                label={aliasLabel(
                                                                                                    alias.collection,
                                                                                                    alias.documentId
                                                                                                )}
                                                                                                options={aliasOptions}
                                                                                                disabled={false}
                                                                                            />
                                                                                            <TextField
                                                                                                className=""
                                                                                                name={`aliases[${index}].key`}
                                                                                                label="Key"
                                                                                                disabled={false}
                                                                                            />
                                                                                            <TextField
                                                                                                className=""
                                                                                                name={`aliases[${index}].value`}
                                                                                                label="Value"
                                                                                                disabled={false}
                                                                                            />
                                                                                            <TextField
                                                                                                className=""
                                                                                                name={`aliases[${index}].comment`}
                                                                                                label="Comment"
                                                                                                disabled={false}
                                                                                            />
                                                                                        </fieldset>
                                                                                    </div>
                                                                                    <div className="w-full md:w-2/12 px-2 mb-4">
                                                                                        <Button
                                                                                            variant="danger"
                                                                                            size="sm"
                                                                                            className="mr-1 mt-2"
                                                                                            type="button"
                                                                                            onClick={() => arrayHelpers.remove(index)}
                                                                                        >
                                                                                            -
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="primary"
                                                                                            size="sm"
                                                                                            className="mt-2"
                                                                                            type="button"
                                                                                            onClick={() =>
                                                                                                arrayHelpers.insert(index, defaultAlias)
                                                                                            }
                                                                                        >
                                                                                            +
                                                                                        </Button>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <Button
                                                                                variant="primary"
                                                                                size="sm"
                                                                                className="ml-1"
                                                                                type="button"
                                                                                onClick={() => arrayHelpers.push(defaultAlias)}
                                                                            >
                                                                                +
                                                                            </Button>
                                                                        )}
                                                                    </Fragment>
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="w-full xl:w-6/12 px-2 mt-3 mb-4"></div>
                                                    </div>
                                                    {data && data.instrument && Array.isArray(data.instrument.extensions) ? (
                                                        <div className="flex flex-wrap -mx-2">
                                                            <div className="w-full xl:w-6/12 px-2 mt-3 mb-4">
                                                                <Link to={"/extensions"}>
                                                                    <h5>Client specific extensions</h5>
                                                                </Link>
                                                                <Link to={"/extensions/new?instrumentId=" + data.instrument._id}>new</Link>
                                                                {data.instrument.extensions.map((extension) => {
                                                                    return (
                                                                        <div
                                                                            key={extension._id}
                                                                            style={{
                                                                                border: "1px solid #ddd",
                                                                                borderRadius: "5px",
                                                                                padding: "10px",
                                                                                marginBottom: "10px",
                                                                                backgroundColor: "#f9f9f9"
                                                                            }}
                                                                        >
                                                                            <div style={{ marginTop: "5px" }}>
                                                                                <strong>{"_id: "}</strong>
                                                                                <Link to={"/extensions/" + extension._id}>
                                                                                    {extension._id}
                                                                                </Link>
                                                                            </div>
                                                                            <div style={{ marginTop: "5px" }}>
                                                                                <strong>{"client: "}</strong>
                                                                                <Link to={"/parties/" + extension.clientId}>
                                                                                    {extension.client.name}
                                                                                </Link>
                                                                            </div>
                                                                            <div style={{ marginTop: "5px" }}>
                                                                                <strong>Key:</strong> {extension.key}
                                                                            </div>
                                                                            <div>
                                                                                <strong>Value:</strong> {extension.value}
                                                                            </div>
                                                                            <div>
                                                                                <strong>Comment:</strong> {extension.comment}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="w-full lg:w-4/12 md:w-4/12 sm:w-3/12 px-2 mb-4">
                                                    <SearchMultipleSelectField
                                                        name="clientIds"
                                                        label="Clients (owner)"
                                                        className="w-100"
                                                        disabled={isSubmitting}
                                                        options={clients.map((d) => ({ key: d._id, value: d.name, text: d.name }))}
                                                    />

                                                    <div className="flex flex-wrap -mx-2 flex-grow">
                                                        <div className="w-full sm:w-full md:w-8/12 px-2 mb-4">
                                                            <TextField
                                                                name="bloombergTicker"
                                                                label="Bloomberg ticker"
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="w-full sm:w-full md:w-4/12 px-2 mb-4">
                                                            <NumberField
                                                                name="bloombergQuoteMultiplier"
                                                                label="Multiplier"
                                                                type="number"
                                                                className=""
                                                                disabled={isSubmitting}
                                                                min={0}
                                                                max={10000}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2 flex-grow">
                                                        <div className="w-full sm:w-full md:w-8/12 px-2 mb-4">
                                                            <TextField
                                                                name="exchange"
                                                                label="Exchange"
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>

                                                        <div className="w-full sm:w-full md:w-4/12 px-2 mb-4">
                                                            <TextField
                                                                name="mic"
                                                                label="MIC"
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <TextField
                                                                name="isin"
                                                                label={
                                                                    <a
                                                                        href={
                                                                            "https://search.gleif.org/#/search/simpleSearch=" + values.isin
                                                                        }
                                                                        target={"_blank"}
                                                                        rel="noreferrer"
                                                                    >
                                                                        {"Isin"}
                                                                    </a>
                                                                }
                                                                type="text"
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                        <div className="w-full sm:w-full md:w-6/12 lg:w-6/12 px-2 mb-4">
                                                            <SelectField
                                                                name="cicArea"
                                                                label="cicArea"
                                                                options={Object.keys(CicAreaEnum)}
                                                                className=""
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-full lg:w-full px-2 mb-4">
                                                            <SearchListField
                                                                className=""
                                                                name="cicType"
                                                                label={"cicType"}
                                                                options={CIC_TYPE_OPTIONS}
                                                                disabled={isSubmitting}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2">
                                                        <div className="w-full sm:w-full md:w-full lg:w-full px-2 mb-4">
                                                            <TextField
                                                                name="cic"
                                                                label="Cic (computed from cicArea and cicType)"
                                                                type="text"
                                                                className=""
                                                                disabled={true}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap -mx-2 flex-grow">
                                                        <div className="w-full px-2">
                                                            <CheckboxItem name="bloombergDailyDownload" label="Bloomberg daily download" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Form>
                                    </div>
                                );
                            }}
                        </Formik>
                    ) : null}
                </div>

                {data && data.instrument ? (
                    <div>
                        {!isCreateMode && data.instrument.model && (
                            <div className="flex flex-wrap -mx-2 mb-3">
                                <div className="w-full px-2">
                                    <Button type="button" variant="primary" size="sm" onClick={() => setModelModal({ showModal: true })}>
                                        View Model
                                    </Button>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-wrap -mx-2 h-full">
                            <div className="w-full lg:w-6/12 px-2 mt-3 mb-4">
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full xs:w-8/12 md:w-8/12 lg:w-8/12 px-2 mb-4">
                                        <h4>update json</h4>
                                    </div>
                                    <div className="w-full xs:w-4/12 md:w-4/12 lg:w-4/12 px-2 mb-4">
                                        <CopyClipboardButton
                                            id="source-json"
                                            text={JSON.stringify(cleanJsonInput(data.instrument), null, 2)}
                                        ></CopyClipboardButton>
                                    </div>
                                </div>
                                <div className="border">
                                    <pre>{JSON.stringify(data.instrument, null, 2)}</pre>
                                </div>
                            </div>
                            <div className="w-full lg:w-6/12 px-2 mt-3 mb-4 h-full">
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full xs:w-8/12 md:w-8/12 lg:w-8/12 px-2 mb-4">
                                        <h4>update json</h4>
                                    </div>
                                    <div className="w-full xs:w-4/12 md:w-4/12 lg:w-4/12 px-2 mb-4">
                                        <CopyClipboardButton id="target-json" text={jsonString}></CopyClipboardButton>
                                    </div>
                                </div>
                                <form>
                                    <textarea
                                        className="form-input"
                                        id="jsonInstrument"
                                        name="jsonInstrument"
                                        rows={30}
                                        value={jsonString}
                                        onChange={handleJsonChange}
                                    />
                                    <Button
                                        type="submit"
                                        variant="danger"
                                        size="sm"
                                        className="mt-2"
                                        onClick={handleJsonSubmit}
                                        disabled={!jsonValidation.valid || !jsonString}
                                    >
                                        Update instrument
                                    </Button>
                                    <Button type="button" variant="info" size="sm" className="mt-2 ml-2" onClick={handleJsonReset}>
                                        Init
                                    </Button>

                                    {!jsonValidation.valid ? (
                                        <Alert
                                            className="mt-3"
                                            color={jsonValidation.valid ? "success" : "danger"}
                                            onClose={onDismissValidationAlert}
                                            dismissible
                                        >
                                            {jsonValidation.message}
                                        </Alert>
                                    ) : null}
                                </form>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
